/* eslint-disable no-console */
import { Flex } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { IconButton, MoveDotsIcon, TrashIcon } from 'ui-components';
import { ISearchContentResult } from '~/searchApi';

import { usePageBuilderStore } from '../../hooks/usePageBuilderStore';
import { AnyBlock } from '../../types';
import { ReorderItem } from './ReorderGrid/ReorderGrid';
import { SingleContent } from './SingleContent';
import {
  MKTalentContentFields,
  TalentContentProperties,
} from './TalentContent';

interface DragHandleProps {
  value: MKTalentContentFields;
}

interface DraggableContentProps {
  content: ISearchContentResult;
  index: number;
  items: TalentContentProperties['customContent'];
  blockId: string;
  showMetrics: boolean;
}

/**
 * DragHandle component for reordering content items
 * @param {DragHandleProps} props - Component props
 */
const DragHandle = ({ value }: DragHandleProps) => {
  const { listeners, isDragging } = useSortable({ id: value.id });

  return (
    <IconButton
      {...listeners}
      aria-label="Move content item"
      icon={<MoveDotsIcon w={4} color="black" />}
      variant="tertiary"
      onClick={(e) => e.stopPropagation()}
      size="sm"
      trackId="media-kit-content-reorder-btn"
      isDisabled={isDragging}
      _active={{ cursor: 'grabbing' }}
      sx={{
        cursor: 'grab',
      }}
    />
  );
};

/**
 * DraggableContent component for displaying and managing draggable content items
 * @param {DraggableContentProps} props - Component props
 */
export const DraggableContent = ({
  content,
  index,
  items,
  blockId,
  showMetrics,
}: DraggableContentProps) => {
  const { updateBlock, blocks, removeBlock } = usePageBuilderStore();
  const value = items[index];

  if (!content || !blockId) {
    console.error('Missing required props in DraggableContent');
    return null;
  }

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();
      e.preventDefault();

      const block = blocks.find((b) => b.id === blockId);
      if (!block) {
        console.error(`Block with id ${blockId} not found`);
        return;
      }

      const newContent = (
        block.properties as TalentContentProperties
      ).customContent.filter((c) => c.id !== content.id);

      // if content is the last item, remove the block
      if (newContent.length === 0) {
        await removeBlock(blockId);
        return;
      }

      // Otherwise update the block with the new content
      await updateBlock(blockId, {
        ...block,
        properties: {
          ...(block.properties as TalentContentProperties),
          customContent: newContent,
        },
      } as AnyBlock);
    } catch (error) {
      console.error('Error deleting content:', error);
      // Here you might want to add error handling UI feedback
    }
  };

  return (
    <ReorderItem
      key={content.contentId}
      value={value}
      borderRadius="lg"
      overflow="hidden"
      position="relative"
      _hover={{
        '.item-action-buttons': {
          visibility: 'visible',
        },
      }}
      className="shrink-0 grow snap-start tab:max-w-[900px] last:tab:max-w-[500px]"
      dragControls
    >
      <Flex
        gap={1.5}
        position="absolute"
        top="10px"
        right="10px"
        zIndex={1000}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className="item-action-buttons"
        visibility="hidden"
      >
        <DragHandle value={value} />
        <IconButton
          onClick={handleDelete}
          aria-label="Delete content item"
          icon={<TrashIcon w={4} color="black" />}
          variant="tertiary"
          size="sm"
          trackId="media-kit-content-delete-btn"
        />
      </Flex>
      <SingleContent content={content} showInShared={showMetrics} />
    </ReorderItem>
  );
};
